import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, XCircle, Users, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function JoinClass() {
  const { code } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("working"); // working | anon | error
  const [error, setError] = useState("");
  const attempted = useRef(false);

  useEffect(() => {
    if (user === null) return;
    if (user === false) { setStatus("anon"); return; }
    if (attempted.current) return;
    attempted.current = true;
    (async () => {
      try {
        const { data } = await api.post("/classes/join", { join_code: code.trim().toUpperCase() });
        toast.success(`Joined "${data.name}"`);
        nav("/dashboard");
      } catch (e) {
        const detail = formatApiErrorDetail(e.response?.data?.detail) || "";
        if (/already joined/i.test(detail)) { nav("/dashboard"); return; }
        setError(detail || "Could not join this class");
        setStatus("error");
      }
    })();
  }, [user, code, nav]);

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center" data-testid="join-class-page">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Users size={26} /></span>
        <p className="mt-4 font-mono text-xs uppercase tracking-widest text-primary">Class invite</p>
        <h1 className="mt-2 font-serif text-3xl">Join code <span className="tracking-widest">{code}</span></h1>

        {status === "working" && (
          <div className="mt-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin" size={18} /> Joining your class…</div>
        )}
        {status === "anon" && (
          <>
            <p className="mt-4 text-muted-foreground">Sign in or create a free account to join this class and see your assignments.</p>
            <div className="mt-6 flex gap-3">
              <Link to="/login" state={{ from: `/join/${code}` }} data-testid="join-login">
                <Button className="rounded-full bg-primary text-foreground hover:bg-primary/90"><LogIn size={16} className="mr-1" /> Log in</Button>
              </Link>
              <Link to="/register" state={{ from: `/join/${code}` }} data-testid="join-register">
                <Button variant="outline" className="rounded-full border-border">Create account</Button>
              </Link>
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mt-6 text-red-600 dark:text-red-400" size={40} />
            <p className="mt-3 text-muted-foreground" data-testid="join-error">{error}</p>
            <Link to="/dashboard" className="mt-6"><Button className="rounded-full bg-primary text-foreground">Go to dashboard</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
